import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomDatePicker from 'Components/CustomDatePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import RichTextEditor from 'Components/TextEditor'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { addEmptyAuthorityOptionForSuperusers } from 'Services/common/Features/Authority/Context/utils'
import { useAutoSelectAuthority } from 'Services/common/Features/Authority/Hooks/useAutoSelectAuthority'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { LogDialog } from 'Services/SalesManagement/Features/Notification/Components/LogDialog'
import useNotificationDetail from 'Services/SalesManagement/Features/Notification/Hooks/useNotificationDetail'
import {
  type AuthorityNotificationDetailShape,
  changeAuthorityNotification,
  createAuthorityNotification,
  deleteAuthorityNotification,
  fetchAuthorityNotification,
  fetchAuthorityNotificationOptions,
  NotificationType
} from 'Services/SalesManagement/Features/Notification/notificationSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  DialogContentText,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export function AuthorityNotificationDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/notifications#${NotificationType.authority}`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const { authorities } = useAuthorityContext()

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)
  const isNewInstance = Number.isNaN(instanceIdParam)

  const initialAuthorityNotificationDetails: AuthorityNotificationDetailShape = {
    id: null,
    active_from: null,
    active_to: null,
    authority_code: '',
    type: '',
    content_en: '',
    content_fi: '',
    content_sv: '',
    edit_events: []
  }

  const fieldErrorsDefaultState = {
    active_from: [],
    active_to: [],
    authority_code: [],
    type: [],
    content_en: [],
    content_fi: [],
    content_sv: []
  }

  const {
    notificationDetails,
    setNotificationDetails,
    tabValue,
    fieldErrors,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isDeleteActionLoading,
    isLogDialogOpen,
    formRef,
    fiEditor,
    setFiEditor,
    enEditor,
    setEnEditor,
    svEditor,
    setSvEditor,
    handleTabChange,
    handleSelectChange,
    handleDatePickerChange,
    handleSubmit,
    handleSave,
    handleDelete,
    onSaveClick,
    onDelete,
    viewLog,
    closeLog
  } = useNotificationDetail<AuthorityNotificationDetailShape>({
    initialDetails: initialAuthorityNotificationDetails,
    initialFieldErrors: fieldErrorsDefaultState,
    instanceIdParam: instanceIdParam,
    isNewInstance: isNewInstance,
    previousLocation: previousLocation,
    createAction: createAuthorityNotification,
    updateAction: changeAuthorityNotification,
    deleteAction: deleteAuthorityNotification,
    messages: {
      createSuccess: t`Authority notification successfully added.`,
      createError: t`There was an error creating the notification.`,
      updateSuccess: t`Authority notification successfully updated.`,
      updateError: t`There was an error editing the notification.`,
      deleteSuccess: t`Authority notification successfully deleted.`,
      deleteError: t`There was an error deleting the notification.`
    }
  })

  const pageTitle = isNewInstance
    ? t`Create new authority notification`
    : t`Edit authority notification`

  const { authorityNotificationOptions, loading, submitLoading } = useTypedSelector(
    state => state.notification
  )

  useEffect(() => {
    dispatch(fetchAuthorityNotificationOptions())
    if (isNewInstance) {
      setNotificationDetails(initialAuthorityNotificationDetails)
    } else {
      dispatch(
        fetchAuthorityNotification({
          id: instanceIdParam
        })
      )
        .then(unwrapResult)
        .then(res => {
          setNotificationDetails(res)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the notification.`)
        })
    }
  }, [dispatch, isNewInstance, instanceIdParam, setNotificationDetails])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useAutoSelectAuthority(isNewInstance, authorities ?? [], setNotificationDetails)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Notifications`,
      link: baseUrl
    },
    {
      title: pageTitle
    }
  ]

  const user = useTypedSelector(state => state.user.user)

  const authorityOptions = addEmptyAuthorityOptionForSuperusers(user, authorities ?? [])

  const isLoading = loading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
          <Box
            sx={{
              display: 'flex',
              maxWidth: 800,
              justifyContent: 'space-between',
              alignItems: 'start'
            }}
          >
            <PageTitle>{pageTitle}</PageTitle>
            {!isNewInstance && (
              <>
                <DeleteButton onClick={onDelete}>{t`Delete notification`}</DeleteButton>
                <DeleteDialog
                  open={isDeleteDialogOpen}
                  handleClose={() => setIsDeleteDialogOpen(false)}
                  handleSubmit={handleDelete}
                  loading={isDeleteActionLoading}
                  title={t`Delete notification`}
                >
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to delete the notification?`}
                  </Typography>
                </DeleteDialog>
              </>
            )}
          </Box>
          {!isNewInstance && isSuperuser(user) && (
            <>
              <CustomButton size="small" onClick={viewLog}>
                {t`View log`}
              </CustomButton>
              <LogDialog
                open={isLogDialogOpen}
                handleClose={closeLog}
                log={notificationDetails.edit_events}
              />
            </>
          )}
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required={!isSuperuser(user)}>
                  <InputLabel>{t`Authority`}</InputLabel>
                  <Select
                    value={notificationDetails.authority_code ?? ''}
                    label={t`Authority`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'authority_code')}
                    error={fieldErrors.authority_code?.length > 0}
                  >
                    {authorityOptions?.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrors error={fieldErrors.authority_code} />
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required>
                  <InputLabel>{t`Type`}</InputLabel>
                  <Select
                    value={notificationDetails.type}
                    label={t`Type`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'type')}
                    error={fieldErrors.type?.length > 0}
                  >
                    {authorityNotificationOptions.type.map(option => (
                      <MenuItem key={option[0]} value={option[0]}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrors error={fieldErrors.type} />
                </FormControl>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CustomDatePicker
                    name="active_from"
                    value={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompare={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompareFieldName="active_to"
                    isStartDate={true}
                    onChange={handleDatePickerChange}
                    label={t`Active from`}
                  />
                  <Box>{` - `}</Box>
                  <CustomDatePicker
                    name="active_to"
                    value={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompare={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompareFieldName="active_from"
                    isStartDate={false}
                    onChange={handleDatePickerChange}
                    label={t`Active to`}
                  />
                </Box>
              </Box>
              <Box>
                <StyledLabel>{t`Content`}</StyledLabel>
                <TabContext value={tabValue}>
                  <Box sx={{ backgroundColor: 'background.paper' }}>
                    <Paper>
                      <TabList onChange={handleTabChange} aria-label={t`Notification tab list`}>
                        <Tab label="FI" value="fi" />
                        <Tab label="SV" value="sv" />
                        <Tab label="EN" value="en" />
                      </TabList>
                    </Paper>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="fi">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_fi ?? ''}
                          setEditor={setFiEditor}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          editor={fiEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.content_fi} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="sv">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_sv ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setSvEditor}
                          editor={svEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.content_sv} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="en">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_en ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setEnEditor}
                          editor={enEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.content_en} />
                      </Box>
                    </TabPanel>
                  </Box>
                </TabContext>
              </Box>
              <SaveDialog
                open={isSaveDialogOpen}
                handleClose={() => setIsSaveDialogOpen(false)}
                handleSubmit={handleSubmit}
                loading={submitLoading === 'pending'}
                title={t`Save notification`}
              >
                <DialogContentText>
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to save the notification?`}
                  </Typography>
                </DialogContentText>
              </SaveDialog>
              <CustomLoadingButton
                isLoading={isSubmitLoading}
                onClick={onSaveClick}
                size="large"
                iconPosition="end"
              >
                {t`Save notification`}
              </CustomLoadingButton>
            </StyledForm>
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
